import React from 'react';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function FAQPage() {
  const { language } = useLanguage();

  const categories = language === 'fr' ? [
    {
      title: 'À propos d\'OFA France',
      faqs: [
        { question: 'Qu\'est-ce qu\'OFA France ?', answer: 'OFA France est un parcours bilingue et international proposé par OFA Lycée pour les élèves scolarisés en France, de la Grande Section à la Terminale. Les élèves restent inscrits dans leur établissement en France tout en développant un véritable parcours anglophone et international avec OFA Lycée.' },
        { question: 'Quelle est la différence entre OFA France et OFA Lycée ?', answer: 'OFA Lycée est notre école mère, fondée aux États-Unis en 2019. OFA France est la branche française d\'OFA Lycée, spécialement conçue pour les élèves du système scolaire français qui souhaitent suivre un programme américain en parallèle de leur scolarité.' },
        { question: 'OFA France est-il accrédité ?', answer: 'Oui. OFA France est accrédité par COGNIA, l\'organisme de référence mondiale en matière d\'accréditation scolaire, qui accrédite plus de 40 000 établissements dans plus de 100 pays. Cette accréditation garantit que notre programme répond aux standards académiques les plus exigeants.' },
      ],
    },
    {
      title: 'Le programme et les cours',
      faqs: [
        { question: 'Mon enfant reste-t-il inscrit dans son école en France ?', answer: 'Oui. OFA France fonctionne en complément de la scolarité principale de l\'élève. Notre objectif est d\'apporter une dimension bilingue, internationale et académique forte sans rupture de parcours.' },
        { question: 'Les cours sont-ils entièrement en ligne ?', answer: 'Oui. Les cours ont lieu en direct via Zoom, en petits groupes de 4 à 8 élèves, avec des enseignants natifs américains certifiés. Notre plateforme de e-learning est Canvas.' },
        { question: 'Combien d\'heures par semaine faut-il prévoir ?', answer: 'Les élèves suivent 1h30 de cours en direct par semaine (1 cours) ou 3h par semaine (2 cours) sur 32 semaines, auxquelles s\'ajoutent du travail personnel. L\'organisation est pensée pour rester compatible avec une scolarité en France.' },
        { question: 'Quand ont lieu les cours ?', answer: 'Les sessions sont programmées en dehors des heures de l\'école française — en fin de journée, le mercredi et le samedi — pour s\'intégrer naturellement dans l\'emploi du temps familial.' },
        { question: 'Quelle est la taille des groupes ?', answer: 'Les groupes sont volontairement limités à 4 à 8 élèves maximum afin de favoriser la participation, les échanges et un accompagnement individualisé.' },
        { question: 'Qui sont les enseignants ?', answer: 'Nos enseignants sont natifs américains, certifiés et expérimentés dans l\'enseignement bilingue et international. Ils dispensent les cours en direct depuis les États-Unis.' },
        { question: 'Quelle est la différence entre OFA France et des cours d\'anglais classiques ?', answer: 'OFA France ne propose pas uniquement de l\'anglais : les élèves développent des compétences académiques, culturelles et méthodologiques (ELA, US History, débat, écriture académique) dans une logique de parcours bilingue durable menant à un diplôme reconnu.' },
      ],
    },
    {
      title: 'Les parcours',
      faqs: [
        { question: 'Faut-il déjà être bilingue en anglais ?', answer: 'Non, pas forcément. L\'OFA Fluent Track est conçu pour les élèves ayant déjà un niveau avancé en anglais (enfants bilingues, retour d\'expatriation). L\'OFA Bridge Track est destiné aux élèves qui souhaitent progresser vers le bilinguisme avec un accompagnement adapté.' },
        { question: 'Comment savoir quel parcours choisir pour mon enfant ?', answer: 'Lors de l\'admission, notre équipe pédagogique évalue le niveau d\'anglais de votre enfant pour l\'orienter vers le parcours le plus adapté. Vous pouvez aussi consulter notre page de comparaison des parcours pour vous faire une première idée.' },
        { question: 'À partir de quel âge peut-on rejoindre OFA France ?', answer: 'Les élèves peuvent rejoindre OFA France dès la Grande Section (5 ans) et jusqu\'à la Terminale.' },
        { question: 'Peut-on changer de parcours en cours d\'année ?', answer: 'Oui, sous certaines conditions et après évaluation par notre équipe pédagogique. L\'objectif est toujours de placer l\'élève dans l\'environnement qui lui permettra de progresser au mieux.' },
      ],
    },
    {
      title: 'Le diplôme',
      faqs: [
        { question: 'Les élèves peuvent-ils préparer un diplôme américain ?', answer: 'Oui. Les élèves suivant le programme complet d\'OFA France peuvent obtenir un U.S. High School Diploma accrédité COGNIA, reconnu par les universités américaines et internationales.' },
        { question: 'Le U.S. High School Diploma est-il reconnu en France ?', answer: 'Le U.S. High School Diploma est un diplôme américain reconnu internationalement. En France, il constitue un atout majeur pour les candidatures dans les universités et grandes écoles, notamment pour les programmes internationaux et anglophones.' },
        { question: 'Mon enfant peut-il obtenir le Bac français ET le diplôme américain ?', answer: 'Oui, c\'est précisément l\'objectif d\'OFA France. Votre enfant poursuit sa scolarité française normalement (menant au Baccalauréat) tout en accumulant les crédits nécessaires pour le U.S. High School Diploma. Les deux diplômes sont complémentaires.' },
      ],
    },
    {
      title: 'Admission et tarifs',
      faqs: [
        { question: 'Comment se déroule l\'admission ?', answer: 'L\'admission se fait en trois étapes : demande en ligne, évaluation du niveau d\'anglais de l\'enfant, et inscription. Des frais de dossier de 75 € sont demandés pour l\'admission.' },
        { question: 'Combien coûte la scolarité chez OFA France ?', answer: 'Les tarifs pour l\'année 2026-2027 sont de 1 995 €/an pour 1 cours (1h30/semaine, 32 semaines) et 3 990 €/an pour 2 cours (3h/semaine, 32 semaines). Des facilités de paiement et des réductions pour fratries sont disponibles. Consultez notre page Tarifs pour les détails.' },
        { question: 'Peut-on s\'inscrire en cours d\'année ?', answer: 'Oui, des intégrations en cours d\'année sont possibles sous conditions, notamment de places disponibles et après évaluation du niveau de l\'élève.' },
      ],
    },
  ] : [
    {
      title: 'About OFA France',
      faqs: [
        { question: 'What is OFA France?', answer: 'OFA France is a bilingual and international pathway offered by OFA Lycée for students enrolled in France, from Kindergarten to 12th grade. Students remain enrolled in their French school while developing a genuine English-speaking and international pathway with OFA Lycée.' },
        { question: 'What is the difference between OFA France and OFA Lycée?', answer: 'OFA Lycée is our parent school, founded in the United States in 2019. OFA France is the French branch of OFA Lycée, specially designed for students in the French school system who wish to follow an American program alongside their schooling.' },
        { question: 'Is OFA France accredited?', answer: 'Yes. OFA France is accredited by COGNIA, the global leader in school accreditation, which accredits over 40,000 institutions in more than 100 countries. This accreditation ensures our program meets the highest academic standards.' },
      ],
    },
    {
      title: 'Program and Classes',
      faqs: [
        { question: 'Does my child stay enrolled in their French school?', answer: 'Yes. OFA France works as a complement to the student\'s main schooling. Our goal is to bring a strong bilingual, international and academic dimension without disrupting their education.' },
        { question: 'Are courses entirely online?', answer: 'Yes. Classes are held live via Zoom, in small groups of 4 to 8 students, with certified native American teachers. Our e-learning platform is Canvas.' },
        { question: 'How many hours per week should we plan for?', answer: 'Students attend 1.5 hours of live classes per week (2x45min) over 32 weeks, plus approximately 1.5 hours of personal work. In high school, an additional 1.5 hours of American History is required. The schedule is designed to remain compatible with French schooling.' },
        { question: 'When do classes take place?', answer: 'Sessions are scheduled outside French school hours — in the evening, on Wednesdays and Saturdays — to naturally fit into the family schedule.' },
        { question: 'What is the class size?', answer: 'Groups are deliberately limited to 4 to 8 students maximum to encourage participation, interaction and individualized support.' },
        { question: 'Who are the teachers?', answer: 'Our teachers are native Americans, certified and experienced in bilingual and international education. They teach live classes from the United States.' },
        { question: 'How is OFA France different from regular English classes?', answer: 'OFA France doesn\'t just offer English: students develop academic, cultural and methodological skills (ELA, US History, debate, academic writing) as part of a lasting bilingual pathway leading to a recognized diploma.' },
      ],
    },
    {
      title: 'Tracks',
      faqs: [
        { question: 'Does my child need to be bilingual already?', answer: 'Not necessarily. The OFA Fluent Track is designed for students with an advanced English level (bilingual children, returning expats). The OFA Bridge Track is for students who wish to progress toward bilingualism with adapted support.' },
        { question: 'How do I know which track to choose for my child?', answer: 'During admission, our academic team assesses your child\'s English level to guide them to the most suitable track. You can also consult our track comparison page for an initial overview.' },
        { question: 'What is the minimum age to join OFA France?', answer: 'Students can join OFA France from Kindergarten (age 5) through 12th grade.' },
        { question: 'Can students switch tracks during the year?', answer: 'Yes, under certain conditions and after assessment by our academic team. The goal is always to place the student in the environment that will allow them to progress best.' },
      ],
    },
    {
      title: 'The Diploma',
      faqs: [
        { question: 'Can students earn an American diploma?', answer: 'Yes. Students following the full OFA France program can earn a COGNIA-accredited U.S. High School Diploma, recognized by American and international universities.' },
        { question: 'Is the U.S. High School Diploma recognized in France?', answer: 'The U.S. High School Diploma is an internationally recognized American diploma. In France, it is a major asset for applications to universities and grandes écoles, especially for international and English-taught programs.' },
        { question: 'Can my child get both the French Bac AND the American diploma?', answer: 'Yes, that is precisely the goal of OFA France. Your child continues their French education normally (leading to the Baccalauréat) while accumulating the credits needed for the U.S. High School Diploma. Both diplomas are complementary.' },
      ],
    },
    {
      title: 'Admission and Tuition',
      faqs: [
        { question: 'How does admission work?', answer: 'Admission is a three-step process: online application, English level assessment, and enrollment. A €75 application fee is required for admission.' },
        { question: 'How much does OFA France cost?', answer: 'Tuition for 2026-2027 is €1,995/year for 1 course (1.5h/week, 32 weeks) and €3,990/year for 2 courses (3h/week, 32 weeks). Payment plans and sibling discounts are available. Check our Tuition page for details.' },
        { question: 'Can students enroll mid-year?', answer: 'Yes, mid-year enrollment is possible under certain conditions, subject to availability and after assessment of the student\'s level.' },
      ],
    },
  ];

  // Flatten all FAQs for schema
  const allFaqs = categories.flatMap(c => c.faqs);

  return (
    <div data-testid="faq-page" className="min-h-screen bg-white">
      <SEO path="/faq" />
      <Navigation />

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allFaqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      })}} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-[#2D78F6] hover:text-[#14274B] transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <Home className="w-4 h-4" />
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Link>
            <ChevronRight className="w-3 h-3 text-[#475569]/50" />
            <span className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>FAQ</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-light text-[#14274B] leading-tight tracking-tight mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently Asked Questions'}
          </motion.h1>
          <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Retrouvez toutes les réponses à vos questions sur OFA France, nos programmes, l\'admission et le diplôme américain.'
              : 'Find all the answers to your questions about OFA France, our programs, admission and the American diploma.'}
          </p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {categories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 * catIndex }}
            >
              <h2 className="text-2xl font-semibold text-[#14274B] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {category.title}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {category.faqs.map((faq, faqIndex) => (
                  <AccordionItem key={faqIndex} value={`cat${catIndex}-faq${faqIndex}`} className="border border-[#E7EAEF] px-6">
                    <AccordionTrigger className="text-[#14274B] font-medium text-left" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#14274B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Vous n\'avez pas trouvé votre réponse ?' : 'Didn\'t find your answer?'}
          </h2>
          <p className="text-white/70 mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Notre équipe est là pour vous aider. Contactez-nous ou participez à nos portes ouvertes virtuelles.'
              : 'Our team is here to help. Contact us or attend our virtual open house.'}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <button className="px-6 py-3 bg-[#2D78F6] text-white font-medium hover:bg-white hover:text-[#14274B] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              </button>
            </Link>
            <Link to="/events">
              <button className="px-6 py-3 border border-white/30 text-white font-medium hover:bg-white hover:text-[#14274B] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Portes ouvertes virtuelles' : 'Virtual Open House'}
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
